import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from '../../helpers/axios';
import { generatePublicUrl } from '../../urlConfig';

const TYPE_META = {
    site_product: { label: 'Каталог сайта', singular: 'Товар', icon: '▣', placeholder: 'Название, артикул…' },
    site_order: { label: 'Заказы сайта', singular: 'Заказ', icon: '▤', placeholder: 'Номер, клиент…' },
    order: { label: 'Заказы', singular: 'Заказ', icon: '▤', placeholder: 'Номер, клиент…' },
    product: { label: 'Товары', singular: 'Товар', icon: '▣', placeholder: 'Название, артикул…' },
    task: { label: 'Задания', singular: 'Задание', icon: '✓', placeholder: 'Название задания…' },
    member: { label: 'Сотрудники', singular: 'Сотрудник', icon: '●', placeholder: 'Имя, email…' },
    sales_session: { label: '3D-сессии', singular: '3D-сессия', icon: '◈', placeholder: 'ID или название…' },
    warehouse_material: { label: 'Склад', singular: 'Материал', icon: '▦', placeholder: 'Название, SKU, остаток…' },
};

// The same existing endpoints are used by the MOBILE ADMIN entity picker.
const escapeRegex = (text) => String(text || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const pickImage = (product) => {
    if (product?.reducedImage) return product.reducedImage;
    const pictures = Array.isArray(product?.productPictures) ? product.productPictures : [];
    const first = pictures.find((item) => item?.img || item?.url || item?.src);
    return first?.img || first?.url || first?.src || '';
};
const imageSrc = (value) => {
    const image = String(value || '');
    if (!image) return '';
    if (/^(https?:)?\/\//i.test(image) || /^(blob:|data:image\/)/i.test(image) || image.startsWith('/media/')) return image;
    return generatePublicUrl(image);
};

const getItems = async ({ officeId, type, query, page }) => {
    if (type === 'site_product') {
        const { data } = await axios.get('/admin/product/getProducts/search', {
            params: { search: query, page: page - 1, pageSize: 25, sort: 'allParameters' },
        });
        return {
            items: (Array.isArray(data?.products) ? data.products : []).filter((item) => item?._id).map((item) => ({
                type,
                id: String(item._id),
                title: item.fullName || item.name || 'Товар',
                subtitle: [item.categoryName, Number(item.price) > 0 ? `${Number(item.price).toLocaleString('ru-RU')} ₽` : ''].filter(Boolean).join(' · '),
                image: pickImage(item),
            })),
            hasMore: page < Number(data?.totalPages || 0),
        };
    }
    if (type === 'site_order') {
        const { data } = await axios.get('/orders', {
            params: { q: escapeRegex(query), page, limit: 25, sort: 'createdAtDesc' },
        });
        return {
            items: (Array.isArray(data?.orders) ? data.orders : []).filter((item) => item?._id).map((item) => ({
                type,
                id: String(item._id),
                title: `Заказ №${[item.orderNumber, item.orderNumberAdditional].filter(Boolean).join(' ') || item._id}`,
                subtitle: [item.user?.firstName, item.user?.lastName, Number(item.totalAmount) > 0 ? `${Number(item.totalAmount).toLocaleString('ru-RU')} ₽` : ''].filter(Boolean).join(' · '),
            })),
            hasMore: page * 25 < Number(data?.total || 0),
        };
    }
    if (!officeId) return { items: [], hasMore: false };
    const { data } = await axios.get(`/office/${encodeURIComponent(officeId)}/chat-entities/${encodeURIComponent(type)}`, {
        params: { q: query, page, limit: 25 },
    });
    return { items: Array.isArray(data?.items) ? data.items : [], hasMore: !!data?.hasMore };
};

export default function ChatEntityPickerModal({ open, officeId, onClose, onSelect, sending = false }) {
    const [types, setTypes] = useState([]);
    const [activeType, setActiveType] = useState('');
    const [query, setQuery] = useState('');
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [selectingId, setSelectingId] = useState('');
    const [error, setError] = useState('');

    const meta = TYPE_META[activeType] || TYPE_META.order;

    useEffect(() => {
        if (!open) return undefined;
        let alive = true;
        setLoading(true);
        setError('');
        setItems([]);
        setTypes([]);
        setActiveType('');

        (officeId ? axios.get(`/office/${officeId}/chat-entities/meta`) : Promise.resolve({ data: { types: [] } }))
            .then(({ data }) => {
                if (!alive) return;
                const officeTypes = (data?.types || []).map((item) => item?.type).filter((type) => TYPE_META[type]);
                const next = [...new Set([...officeTypes, 'site_product', 'site_order'])];
                setTypes(next);
                setActiveType(next[0] || 'site_product');
            })
            .catch((requestError) => alive && setError(requestError?.response?.data?.error || requestError?.message || 'Не удалось открыть объекты'))
            .finally(() => alive && setLoading(false));

        return () => { alive = false; };
    }, [open, officeId]);

    useEffect(() => {
        if (!open || !activeType) return undefined;
        let alive = true;
        const timer = window.setTimeout(() => {
            setLoading(true);
            setError('');
            getItems({ officeId, type: activeType, query: query.trim(), page: 1 })
                .then((data) => {
                    if (!alive) return;
                    setItems(data.items);
                    setHasMore(data.hasMore);
                    setPage(1);
                })
                .catch((requestError) => alive && setError(requestError?.response?.data?.error || requestError?.message || 'Ошибка поиска'))
                .finally(() => alive && setLoading(false));
        }, 220);
        return () => { alive = false; clearTimeout(timer); };
    }, [open, officeId, activeType, query]);

    const loadMore = async () => {
        if (!hasMore || loadingMore) return;
        const nextPage = page + 1;
        setLoadingMore(true);
        try {
            const data = await getItems({ officeId, type: activeType, query: query.trim(), page: nextPage });
            setItems((prev) => [...prev, ...data.items]);
            setHasMore(data.hasMore);
            setPage(nextPage);
        } catch (requestError) {
            setError(requestError?.response?.data?.error || requestError?.message || 'Ошибка загрузки');
        } finally { setLoadingMore(false); }
    };

    const selectItem = async (item) => {
        if (!item?.id || selectingId || sending) return;
        setSelectingId(String(item.id));
        try {
            const ok = await onSelect?.(item);
            if (ok !== false) onClose?.();
        } finally { setSelectingId(''); }
    };

    const title = useMemo(() => meta?.label || 'Объекты', [meta]);
    if (!open || typeof document === 'undefined') return null;

    return createPortal(
        <div className="chat-entity-picker-AO" role="dialog" aria-modal="true">
            <button type="button" className="chat-entity-picker-AO__backdrop" onClick={onClose} aria-label="Закрыть" />
            {/* Окно — с теми же классами, что на фронте: для них уже есть стили. */}
            <section className="chat-entity-picker-AO__dialog">
                <header className="chat-entity-picker-AO__head">
                    <div>
                        <span>Прикрепить к сообщению</span>
                        <h2>{title}</h2>
                        <p>Товар каталога, заказ или объект офиса — придёт в чат карточкой.</p>
                    </div>
                    <button type="button" onClick={onClose} disabled={!!selectingId} aria-label="Закрыть">×</button>
                </header>
                <div className="chat-entity-picker-AO__tabs">
                    {types.map((type) => (
                        <button key={type} type="button" className={type === activeType ? 'is-active' : ''} onClick={() => { setActiveType(type); setQuery(''); }}>
                            <span>{TYPE_META[type]?.icon || '◆'}</span>{TYPE_META[type]?.label || type}
                        </button>
                    ))}
                </div>
                {activeType ? (
                    <>
                        <div className="chat-entity-picker-AO__search">
                            <span>⌕</span>
                            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={meta.placeholder} />
                            {query ? <button type="button" onClick={() => setQuery('')} aria-label="Очистить">×</button> : null}
                        </div>
                        <div className="chat-entity-picker-AO__body">
                            {loading ? <div className="chat-entity-picker-AO__state">Ищем…</div>
                                : error ? <div className="chat-entity-picker-AO__state is-error">{error}</div>
                                : items.length ? <div className="chat-entity-picker-AO__list">{items.map((item) => (
                                    <button type="button" key={`${item.type}:${item.id}`} className="chat-entity-picker-AO__item" onClick={() => selectItem(item)} disabled={sending || !!selectingId}>
                                        <div className="chat-entity-picker-AO__item-icon">
                                            {item.image ? <img src={imageSrc(item.image)} alt="" loading="lazy" /> : <span>{TYPE_META[item.type]?.icon || '◆'}</span>}
                                        </div>
                                        <div className="chat-entity-picker-AO__item-main"><strong>{item.title || meta.singular}</strong>{item.subtitle ? <p>{item.subtitle}</p> : null}</div>
                                        <div className="chat-entity-picker-AO__item-action">{selectingId === String(item.id) ? <i /> : <span>Прикрепить</span>}</div>
                                    </button>
                                ))}</div> : <div className="chat-entity-picker-AO__state">Ничего не найдено.</div>}
                            {hasMore && !loading ? <button type="button" className="chat-entity-picker-AO__more" onClick={loadMore} disabled={loadingMore}>{loadingMore ? 'Загружаем…' : 'Показать ещё'}</button> : null}
                        </div>
                    </>
                ) : !loading ? <div className="chat-entity-picker-AO__state">Для этого офиса нет доступных типов объектов.</div> : null}
            </section>
        </div>, document.body
    );
}
